<template>
  <div class="resource-deployment-deploy">
    <!-- Tabbed Deployment Type Selection (only shown if belongable_type is Country) -->
    <div v-if="belongableType === 'Country'" class="bg-white shadow sm:rounded-lg mb-6">
      <!-- Tab Navigation -->
      <div class="border-b border-gray-200 bg-gray-50 px-3 sm:rounded-t-lg">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            @click="deploymentType = 'leader_roles'; onDeploymentTypeChange()"
            :class="deploymentType === 'leader_roles' 
              ? 'border-blue-500 text-blue-600 bg-white border-l border-t border-r rounded-t' 
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
          >
            Leader Roles
          </button>
          <button
            @click="deploymentType = 'training_centers'; onDeploymentTypeChange()"
            :class="deploymentType === 'training_centers' 
              ? 'border-blue-500 text-blue-600 bg-white border-l border-t border-r rounded-t' 
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
          >
            Training Centers
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="px-3 py-4 sm:px-6">
        <!-- Training Centers Tab Content with Filters -->
        <div v-if="deploymentType === 'training_centers'">
          <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Filters</h3>
          
          <!-- Primary Filters (always visible) -->
          <div class="flex flex-wrap items-end gap-4 mb-4" style="overflow: visible;">
            <!-- Training Center Group -->
            <div class="w-64" style="position: relative; z-index: 15;">
              <label class="block text-sm font-medium text-gray-700 mb-2">Training Center Group</label>
              <multiselect
                v-model="selectedTrainingCenterGroup"
                :options="trainingCenterGroupOptions"
                :searchable="true"
                placeholder="Select group..."
                label="name"
                track-by="id"
                :allow-empty="true"
                @input="onFilterChange"
              />
            </div>

            <!-- Sort -->
            <div class="w-48" style="position: relative; z-index: 14;">
              <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <multiselect
                v-model="selectedSort"
                :options="sortOptions"
                :searchable="false"
                placeholder="Select sort..."
                label="name"
                track-by="value"
                :allow-empty="false"
                @select="onFilterChange"
              />
            </div>

            <!-- Spacer to push button right -->
            <div class="flex-grow"></div>

            <!-- Clear Filters Button -->
            <div class="flex items-end">
              <button
                @click="clearFilters"
                class="btn btn-secondary text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <!-- Additional Filters (collapsible) -->
          <div class="mb-6">
            <button
              @click="showAdditionalFilters = !showAdditionalFilters"
              class="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-3"
            >
              <svg
                :class="{ 'rotate-90': showAdditionalFilters }"
                class="w-4 h-4 mr-2 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              Additional Filters
              <span v-if="hasActiveAdditionalFilters" class="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Active</span>
            </button>
            
            <div v-show="showAdditionalFilters" class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 pl-6" style="overflow: visible;">
              <!-- State -->
              <div style="position: relative; z-index: 13;">
                <label class="block text-sm font-medium text-gray-700 mb-2">State</label>
                <multiselect
                  v-model="selectedState"
                  :options="stateOptions"
                  :searchable="false"
                  placeholder="Select state..."
                  label="name"
                  track-by="value"
                  :allow-empty="true"
                  @input="onFilterChange"
                />
              </div>

              <!-- Reporting Milestone -->
              <div style="position: relative; z-index: 12;">
                <label class="block text-sm font-medium text-gray-700 mb-2">Reporting Milestone</label>
                <multiselect
                  v-model="selectedReportingMilestone"
                  :options="reportingMilestoneOptions"
                  :searchable="true"
                  placeholder="Select milestone..."
                  label="name"
                  track-by="id"
                  :allow-empty="true"
                  @input="onFilterChange"
                />
              </div>

              <!-- Start Date From -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Start Date From</label>
                <input
                  type="date"
                  v-model="startDateFrom"
                  @change="onFilterChange"
                  class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <!-- Start Date To -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Start Date To</label>
                <input
                  type="date"
                  v-model="startDateTo"
                  @change="onFilterChange"
                  class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <!-- Timothies Min -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"># Timothies Min</label>
                <input
                  type="number"
                  v-model.number="timothiesMin"
                  min="0"
                  @change="onFilterChange"
                  class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Min"
                />
              </div>

              <!-- Timothies Max -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"># Timothies Max</label>
                <input
                  type="number"
                  v-model.number="timothiesMax"
                  min="0"
                  @change="onFilterChange"
                  class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Max"
                />
              </div>

              <!-- No Previous Deployment -->
              <div class="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="no-previous-deployment"
                  v-model="filterNoPreviousDeployment"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
                <label for="no-previous-deployment" class="ml-2 block text-sm font-medium text-gray-700">
                  No Previous Deployment
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Leader Roles Tab Content -->
        <div v-if="deploymentType === 'leader_roles'">
          <p class="text-sm text-gray-600 mb-4">Select leader roles within the country to deploy resources.</p>
          
          <!-- Leader Roles Filters -->
          <div class="mb-6 p-4 bg-gray-50 rounded-lg">
            <div class="flex flex-wrap items-end gap-4">
              <!-- Role Filter (multi-select) -->
              <div class="w-64">
                <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <multiselect
                  v-model="selectedLeaderRoles"
                  :options="leaderRoleOptions"
                  :multiple="true"
                  :searchable="false"
                  :close-on-select="false"
                  :allow-empty="true"
                  placeholder="All Roles"
                  label="name"
                  track-by="value"
                  @select="onLeaderFilterChange"
                  @remove="onLeaderFilterChange"
                />
              </div>

              <!-- State Filter -->
              <div class="w-48">
                <label class="block text-sm font-medium text-gray-700 mb-2">State</label>
                <multiselect
                  v-model="selectedLeaderState"
                  :options="leaderStateOptions"
                  :searchable="false"
                  :allow-empty="true"
                  placeholder="Active (default)"
                  label="name"
                  track-by="value"
                  @select="onLeaderFilterChange"
                  @remove="onLeaderFilterChange"
                />
              </div>

              <!-- No Previous Deployment -->
              <div class="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="leader-no-previous-deployment"
                  v-model="filterLeaderNoPreviousDeployment"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
                <label for="leader-no-previous-deployment" class="ml-2 block text-sm font-medium text-gray-700">
                  No Previous Deployment
                </label>
              </div>

              <!-- Spacer to push button right -->
              <div class="flex-grow"></div>

              <!-- Clear Filters Button -->
              <div class="flex items-end">
                <button
                  @click="clearLeaderFilters"
                  class="btn btn-secondary text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Bulk Actions (shown in both tabs) -->
        <div v-if="entities.length > 0" class="mb-6">
          <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Bulk Actions</h3>
          <div class="flex items-end space-x-4">
            <div v-if="deploymentType === 'training_centers'" class="w-48">
              <label class="block text-sm font-medium text-gray-700 mb-2">Deploy Mode</label>
              <select
                v-model="bulkDeployMode"
                class="form-select block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="fixed">Fixed Quantity</option>
                <option value="per_timothy">Per Timothy</option>
              </select>
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ bulkDeployMode === 'per_timothy' ? 'Quantity per Timothy' : 'Quantity for Selected Entities' }}
              </label>
              <input
                type="number"
                v-model.number="bulkQuantity"
                min="0"
                :max="bulkDeployMode === 'per_timothy' ? undefined : availableQuantity"
                class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                :placeholder="bulkDeployMode === 'per_timothy' ? 'Qty per timothy' : 'Enter quantity'"
              />
            </div>
            <button
              @click="applyBulkQuantity"
              class="btn btn-primary"
              :disabled="selectedEntityIds.length === 0 || !bulkQuantity || bulkQuantity <= 0"
            >
              Apply to Selected
            </button>
            <button
              @click="clearSelectedQuantities"
              class="btn btn-secondary"
              :disabled="selectedEntityIds.length === 0"
            >
              Clear Selected
            </button>
          </div>
        </div>

        <!-- Resources to Deploy Table (shown in both tabs) -->
        <div v-if="entities.length > 0" class="relative">
          <!-- Loading overlay -->
          <div v-if="loading" class="loading-overlay">
            <div class="inline-flex items-center space-x-2">
              <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-gray-600 font-medium">Loading...</span>
            </div>
          </div>

          <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Resources to Deploy</h3>
          <!-- Deployment Summary and Warning -->
          <div v-if="getTotalDeploymentQuantity() > 0" class="mb-4 p-4 rounded-md" :class="getTotalDeploymentQuantity() > availableQuantity ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <svg v-if="getTotalDeploymentQuantity() > availableQuantity" class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <svg v-else class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3 flex-1">
                <h3 class="text-sm font-medium" :class="getTotalDeploymentQuantity() > availableQuantity ? 'text-red-800' : 'text-blue-800'">
                  <span v-if="getTotalDeploymentQuantity() > availableQuantity">
                    Warning: Total deployment exceeds available quantity
                  </span>
                  <span v-else>
                    Deployment Summary
                  </span>
                </h3>
                <div class="mt-2 text-sm" :class="getTotalDeploymentQuantity() > availableQuantity ? 'text-red-700' : 'text-blue-700'">
                  <p>
                    Total attempting to deploy: <strong>{{ getTotalDeploymentQuantity() }}</strong>
                  </p>
                  <p>
                    Available quantity: <strong>{{ availableQuantity }}</strong>
                    <span v-if="getTotalDeploymentQuantity() > availableQuantity" class="ml-2 font-semibold">
                      ({{ (getTotalDeploymentQuantity() - availableQuantity) }} over limit)
                    </span>
                  </p>
                </div>
                <!-- Bulk Deploy Message -->
                <div v-if="bulkDeployMessage" class="mt-3 pt-3 border-t border-blue-200 text-sm text-blue-700">
                  <p class="font-medium">Bulk deploy applied to {{ bulkDeployMessage.totalSelected }} selected items:</p>
                  <ul class="mt-1 list-disc list-inside">
                    <li>{{ bulkDeployMessage.fullAllocationCount }} received full quantity</li>
                    <li v-if="bulkDeployMessage.partialAllocationCount > 0">{{ bulkDeployMessage.partialAllocationCount }} received partial quantity</li>
                    <li v-if="bulkDeployMessage.noAllocationCount > 0" class="text-amber-600">{{ bulkDeployMessage.noAllocationCount }} received no quantity (insufficient resources)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto mt-4">
            <table class="table-less-column-padding-sticky min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleSelectAll"
                      class="select-all form-checkbox h-4 w-4 text-blue-600"
                    />
                  </th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Belongable</th>
                  <th v-if="deploymentType === 'training_centers' && selectedSort && selectedSort.value !== 'name'" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ sortColumnLabel }}</th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previously Deployed</th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deploy Quantity</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="entity in filteredEntities" :key="entity.id" :class="{ 'bg-red-50': getDeployQuantityError(entity) }">
                  <td class="px-3 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      :value="entity.id"
                      v-model="selectedEntityIds"
                      class="form-checkbox h-4 w-4 text-blue-600"
                    />
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{{ entity.name }}</td>
                  <td v-if="deploymentType === 'training_centers' && selectedSort && selectedSort.value !== 'name'" class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{{ getSortValue(entity) }}</td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span 
                      v-if="entity.deployment_details && entity.deployment_details.length > 0"
                      :title="formatDeploymentDetailsTooltip(entity.deployment_details)"
                      class="cursor-help underline decoration-dotted"
                    >
                      {{ entity.previously_deployed || 0 }}
                    </span>
                    <span v-else>{{ entity.previously_deployed || 0 }}</span>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      v-model.number="entity.deploy_quantity"
                      min="0"
                      :max="getMaxDeployQuantity(entity)"
                      class="quantBoxes form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-2 py-1"
                      :class="{ 'border-red-500': getDeployQuantityError(entity) }"
                      placeholder="0"
                    />
                    <span v-if="getDeployQuantityError(entity)" class="text-xs text-red-500 block mt-1">
                      Max: {{ getMaxDeployQuantity(entity) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Initial Loading State (no entities yet) -->
        <div v-if="loading && entities.length === 0" class="text-center py-8">
          <div class="inline-flex items-center space-x-2">
            <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-gray-600 font-medium">Loading...</span>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && entities.length === 0" class="text-center py-8">
          <p class="text-gray-500">No entities found. Please adjust your filters.</p>
        </div>
      </div>
    </div>

    <!-- TrainingCenterGroup: Show only training centers (no tabs) -->
    <div v-if="belongableType === 'TrainingCenterGroup'" class="bg-white shadow sm:rounded-lg mb-6">
      <div class="px-3 py-4 sm:px-6">
        <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Training Centers</h3>
        <p class="text-sm text-gray-600 mb-6">Deploy resources to training centers within this group.</p>

        <!-- Bulk Actions -->
        <div v-if="entities.length > 0" class="mb-6">
          <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Bulk Actions</h3>
          <div class="flex items-end space-x-4">
            <div class="w-48">
              <label class="block text-sm font-medium text-gray-700 mb-2">Deploy Mode</label>
              <select
                v-model="bulkDeployMode"
                class="form-select block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="fixed">Fixed Quantity</option>
                <option value="per_timothy">Per Timothy</option>
              </select>
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ bulkDeployMode === 'per_timothy' ? 'Quantity per Timothy' : 'Quantity for Selected Entities' }}
              </label>
              <input
                type="number"
                v-model.number="bulkQuantity"
                min="0"
                :max="bulkDeployMode === 'per_timothy' ? undefined : availableQuantity"
                class="form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
                :placeholder="bulkDeployMode === 'per_timothy' ? 'Qty per timothy' : 'Enter quantity'"
              />
            </div>
            <button
              @click="applyBulkQuantity"
              class="btn btn-primary"
              :disabled="selectedEntityIds.length === 0 || !bulkQuantity || bulkQuantity <= 0"
            >
              Apply to Selected
            </button>
            <button
              @click="clearSelectedQuantities"
              class="btn btn-secondary"
              :disabled="selectedEntityIds.length === 0"
            >
              Clear Selected
            </button>
          </div>
        </div>

        <!-- Resources to Deploy Table -->
        <div v-if="entities.length > 0" class="relative">
          <!-- Loading overlay -->
          <div v-if="loading" class="loading-overlay">
            <div class="inline-flex items-center space-x-2">
              <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-gray-600 font-medium">Loading...</span>
            </div>
          </div>

          <h3 class="text-xl leading-6 font-medium text-gray-900 mb-4">Resources to Deploy</h3>
          <!-- Deployment Summary and Warning -->
          <div v-if="getTotalDeploymentQuantity() > 0" class="mb-4 p-4 rounded-md" :class="getTotalDeploymentQuantity() > availableQuantity ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <svg v-if="getTotalDeploymentQuantity() > availableQuantity" class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <svg v-else class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3 flex-1">
                <h3 class="text-sm font-medium" :class="getTotalDeploymentQuantity() > availableQuantity ? 'text-red-800' : 'text-blue-800'">
                  <span v-if="getTotalDeploymentQuantity() > availableQuantity">
                    Warning: Total deployment exceeds available quantity
                  </span>
                  <span v-else>
                    Deployment Summary
                  </span>
                </h3>
                <div class="mt-2 text-sm" :class="getTotalDeploymentQuantity() > availableQuantity ? 'text-red-700' : 'text-blue-700'">
                  <p>
                    Total attempting to deploy: <strong>{{ getTotalDeploymentQuantity() }}</strong>
                  </p>
                  <p>
                    Available quantity: <strong>{{ availableQuantity }}</strong>
                    <span v-if="getTotalDeploymentQuantity() > availableQuantity" class="ml-2 font-semibold">
                      ({{ (getTotalDeploymentQuantity() - availableQuantity) }} over limit)
                    </span>
                  </p>
                </div>
                <!-- Bulk Deploy Message -->
                <div v-if="bulkDeployMessage" class="mt-3 pt-3 border-t border-blue-200 text-sm text-blue-700">
                  <p class="font-medium">Bulk deploy applied to {{ bulkDeployMessage.totalSelected }} selected items:</p>
                  <ul class="mt-1 list-disc list-inside">
                    <li>{{ bulkDeployMessage.fullAllocationCount }} received full quantity</li>
                    <li v-if="bulkDeployMessage.partialAllocationCount > 0">{{ bulkDeployMessage.partialAllocationCount }} received partial quantity</li>
                    <li v-if="bulkDeployMessage.noAllocationCount > 0" class="text-amber-600">{{ bulkDeployMessage.noAllocationCount }} received no quantity (insufficient resources)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto mt-4">
            <table class="table-less-column-padding-sticky min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleSelectAll"
                      class="select-all form-checkbox h-4 w-4 text-blue-600"
                    />
                  </th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Training Center</th>
                  <th v-if="selectedSort && selectedSort.value !== 'name'" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ sortColumnLabel }}</th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previously Deployed</th>
                  <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deploy Quantity</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="entity in filteredEntities" :key="entity.id" :class="{ 'bg-red-50': getDeployQuantityError(entity) }">
                  <td class="px-3 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      :value="entity.id"
                      v-model="selectedEntityIds"
                      class="form-checkbox h-4 w-4 text-blue-600"
                    />
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{{ entity.name }}</td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span 
                      v-if="entity.deployment_details && entity.deployment_details.length > 0"
                      :title="formatDeploymentDetailsTooltip(entity.deployment_details)"
                      class="cursor-help underline decoration-dotted"
                    >
                      {{ entity.previously_deployed || 0 }}
                    </span>
                    <span v-else>{{ entity.previously_deployed || 0 }}</span>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      v-model.number="entity.deploy_quantity"
                      min="0"
                      :max="getMaxDeployQuantity(entity)"
                      class="quantBoxes form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-2 py-1"
                      :class="{ 'border-red-500': getDeployQuantityError(entity) }"
                      placeholder="0"
                    />
                    <span v-if="getDeployQuantityError(entity)" class="text-xs text-red-500 block mt-1">
                      Max: {{ getMaxDeployQuantity(entity) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Initial Loading State (no entities yet) -->
        <div v-if="loading && entities.length === 0" class="text-center py-8">
          <div class="inline-flex items-center space-x-2">
            <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-gray-600 font-medium">Loading...</span>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && entities.length === 0" class="text-center py-8">
          <p class="text-gray-500">No training centers found in this group.</p>
        </div>
      </div>
    </div>

    <!-- Deployment Date and Action Buttons (outside tabs, shared) -->
    <div v-if="belongableType === 'Country' || belongableType === 'TrainingCenterGroup'" class="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      <div class="px-3 py-4 sm:px-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between">
          <div class="max-w-xs flex-1 flex pt-4 mb-4 md:mb-0">
            <label for="deployment-date" class="min-w-[140px] leading-[3]" style="min-width: 140px; line-height: 3;">
              Deployment Date
            </label>
            <input
              id="deployment-date"
              type="text"
              v-model="deploymentDate"
              class="one-off-standard-form-input datepicker form-input block w-full sm:text-sm sm:leading-5 border border-gray-300 rounded-md px-3 py-2"
              placeholder="Click to pick a date..."
            />
          </div>
          <div class="flex justify-end">
            <button
              @click="onCancel"
              class="btn btn-secondary mr-3"
            >
              Cancel
            </button>
            <button
              @click="onDeploy"
              class="btn"
              :class="hasValidationErrors() ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'"
              :disabled="!canDeploy"
              :title="hasValidationErrors() ? 'Please fix quantity errors before deploying' : ''"
            >
              Deploy Resources
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import axios from 'axios'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'

export default {
  name: 'ResourceDeploymentDeploy',
  components: {
    Multiselect,
  },
  props: {
    resourceDeploymentId: {
      type: Number,
      required: true,
    },
    countryId: {
      type: Number,
      required: false,
    },
    belongableType: {
      type: String,
      required: false,
    },
    belongableId: {
      type: Number,
      required: false,
    },
    parentResourceDeploymentId: {
      type: Number,
      required: false,
    },
    resourceId: {
      type: Number,
      required: false,
    },
    resourceDonationId: {
      type: Number,
      required: false,
    },
    totalAllocatedQuantity: {
      type: Number,
      required: false,
    },
    totalDeployedQuantity: {
      type: Number,
      required: false,
    },
    reportingMilestones: {
      type: Array,
      required: false,
      default: () => [],
    },
    trainingCenterStates: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      deploymentType: 'leader_roles', // 'leader_roles' or 'training_centers'
      entities: [],
      selectedEntityIds: [],
      bulkQuantity: null,
      bulkDeployMode: 'fixed',
      bulkDeployMessage: null,
      showAdditionalFilters: false,
      deploymentDate: null,
      datePicker: null,
      loading: false,
      
      // Training center filters
      selectedTrainingCenterGroup: null,
      selectedReportingMilestone: null,
      selectedState: null,
      selectedSort: { value: 'name', name: 'Name' },
      startDateFrom: null,
      startDateTo: null,
      timothiesMin: null,
      timothiesMax: null,
      filterNoPreviousDeployment: false,
      trainingCenterGroupOptions: [],
      // Leader Roles filters
      selectedLeaderState: null,
      selectedLeaderRoles: [],
      filterLeaderNoPreviousDeployment: false,
    }
  },
  computed: {
    filteredEntities() {
      let filtered = this.entities
      
      // Training Centers: No Previous Deployment filter
      if (this.deploymentType === 'training_centers' && this.filterNoPreviousDeployment) {
        filtered = filtered.filter(e => !e.previously_deployed || e.previously_deployed === 0)
      }
      
      // Leader Roles: No Previous Deployment filter
      if (this.deploymentType === 'leader_roles' && this.filterLeaderNoPreviousDeployment) {
        filtered = filtered.filter(e => !e.previously_deployed || e.previously_deployed === 0)
      }
      
      return filtered
    },
    allSelected() {
      return this.filteredEntities.length > 0 && this.selectedEntityIds.length === this.filteredEntities.length
    },
    canDeploy() {
      return (
        this.deploymentDate &&
        this.entities.some(e => e.deploy_quantity && e.deploy_quantity > 0) &&
        !this.hasValidationErrors()
      )
    },
    availableQuantity() {
      const allocated = this.totalAllocatedQuantity || 0
      const deployed = this.totalDeployedQuantity || 0
      return allocated - deployed
    },
    reportingMilestoneOptions() {
      return (this.reportingMilestones || []).map(rm => ({
        id: rm.id,
        name: rm.name,
      }))
    },
    stateOptions() {
      // Use passed states or default to common states
      if (this.trainingCenterStates && this.trainingCenterStates.length > 0) {
        return this.trainingCenterStates.map(s => ({
          value: s.value || s,
          name: s.name || s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' '),
        }))
      }
      // Default state options
      return [
        { value: 'active', name: 'Active' },
        { value: 'under_review', name: 'Under Review' },
        { value: 'graduated', name: 'Graduated' },
        { value: 'suspended', name: 'Suspended' },
        { value: 'dropped', name: 'Dropped' },
      ]
    },
    sortOptions() {
      return [
        { value: 'name', name: 'Name' },
        { value: 'start_date', name: 'Start Date' },
        { value: 'reporting_milestone', name: 'Reporting Milestone' },
        { value: 'num_timothies', name: '# of Timothies' },
      ]
    },
    leaderStateOptions() {
      return [
        { value: 'active', name: 'Active' },
        { value: 'inactive', name: 'Inactive' },
        { value: 'suspended', name: 'Suspended' },
        { value: 'graduated', name: 'Graduated' },
      ]
    },
    leaderRoleOptions() {
      return [
        { value: 'master_trainer', name: 'Master Trainer' },
        { value: 'area_leader', name: 'Area Leader' },
        { value: 'zonal_leader', name: 'Zonal Leader' },
        { value: 'zonal_coordinator', name: 'Zonal Coordinator' },
        { value: 'regional_leader', name: 'Regional Leader' },
        { value: 'movement_leader', name: 'Movement Leader' },
        { value: 'project_supervisor', name: 'Project Supervisor' },
        { value: 'other_staff', name: 'Other Staff' },
      ]
    },
    sortColumnLabel() {
      if (!this.selectedSort) return ''
      switch (this.selectedSort.value) {
        case 'start_date': return 'Start Date'
        case 'reporting_milestone': return 'Reporting Milestone'
        case 'num_timothies': return '# of Timothies'
        default: return ''
      }
    },
    hasActiveAdditionalFilters() {
      return !!(
        this.selectedState ||
        this.selectedReportingMilestone ||
        this.startDateFrom ||
        this.startDateTo ||
        this.timothiesMin ||
        this.timothiesMax ||
        this.filterNoPreviousDeployment
      )
    },
  },
  mounted() {
    this.initializeDatePicker()
    
    // Only load training center groups if we have a country ID
    if (this.countryId) {
      this.loadTrainingCenterGroups()
    }
    
    // Load entities based on belongable_type
    if (this.belongableType === 'Country') {
      this.loadEntities()
    } else if (this.belongableType === 'TrainingCenterGroup') {
      // For TrainingCenterGroup, set deployment type to training_centers and load
      this.deploymentType = 'training_centers'
      this.loadTrainingCenters()
    } else {
      // For other belongable_types, leave entities empty
      this.entities = []
    }
  },
  beforeUnmount() {
    if (this.datePicker) {
      this.datePicker.destroy()
    }
  },
  watch: {
    selectedTrainingCenterGroup(newVal, oldVal) {
      // Reload training centers when filter changes
      if (this.deploymentType === 'training_centers' && newVal !== oldVal) {
        this.selectedEntityIds = []
        this.loadEntities()
      }
    },
    selectedReportingMilestone(newVal, oldVal) {
      // Reload training centers when filter changes
      if (this.deploymentType === 'training_centers' && newVal !== oldVal) {
        this.selectedEntityIds = []
        this.loadEntities()
      }
    },
    selectedSort(newVal, oldVal) {
      // Reload training centers when sort changes
      if (this.deploymentType === 'training_centers' && newVal !== oldVal) {
        this.loadEntities()
      }
    },
    selectedState(newVal, oldVal) {
      // Reload TCG options and training centers when state filter changes
      if (this.deploymentType === 'training_centers' && newVal !== oldVal) {
        this.selectedEntityIds = []
        this.loadTrainingCenterGroups()
        this.loadEntities()
      }
    },
  },
  methods: {
    initializeDatePicker() {
      this.$nextTick(() => {
        const dateInput = document.getElementById('deployment-date')
        if (dateInput) {
          this.datePicker = flatpickr(dateInput, {
            dateFormat: 'Y-m-d',
            onChange: (selectedDates, dateStr) => {
              this.deploymentDate = dateStr
            },
          })
        }
      })
    },
    async loadTrainingCenterGroups() {
      if (!this.countryId) {
        this.trainingCenterGroupOptions = []
        return
      }
      
      try {
        const params = {
          country_id: this.countryId,
          state: this.selectedState?.value || 'active',
        }
        
        const response = await axios.get('/training_center_groups.json', { params })
        const groups = Array.isArray(response.data) ? response.data : (response.data.data || [])
        this.trainingCenterGroupOptions = groups.map(group => ({
          id: group.id || group.attributes?.id,
          name: group.name || group.attributes?.name,
        }))
        
        // Clear TCG selection if the previously selected group is no longer in the options
        if (this.selectedTrainingCenterGroup) {
          const stillExists = this.trainingCenterGroupOptions.some(
            g => g.id === this.selectedTrainingCenterGroup.id
          )
          if (!stillExists) {
            this.selectedTrainingCenterGroup = null
          }
        }
      } catch (error) {
        console.error('Error loading training center groups:', error)
        this.trainingCenterGroupOptions = []
      }
    },
    async loadLeaderRoles() {
      if (!this.countryId) {
        console.error('Country ID is required to load leader roles')
        this.entities = []
        return
      }
      
      this.loading = true
      try {
        // Build request params - use server-side filtering where possible
        const params = {
          country_id: this.countryId,
          role_type: 'LEADER_ROLES',
          role_state: this.selectedLeaderState?.value || 'active',
          include_person_roles: true,
        }
        
        // If a single role is selected, use server-side role filter
        if (this.selectedLeaderRoles.length === 1) {
          params.role = this.selectedLeaderRoles[0].value
        }
        
        const response = await axios.get('/people.json', { params })
        
        const people = Array.isArray(response.data) ? response.data : (response.data.data || [])
        
        // Get deployed quantities for leader roles
        const deployedData = await this.getDeployedQuantities('Person')
        
        // Client-side role filtering for multi-role selection
        let filteredPeople = people
        if (this.selectedLeaderRoles.length > 1) {
          const filterRoles = this.selectedLeaderRoles.map(r => r.value)
          filteredPeople = people.filter(person => {
            const roles = person.person_roles || []
            if (roles.length === 0) return true
            return roles.some(pr => filterRoles.includes(pr.role))
          })
        }
        
        this.entities = filteredPeople
          .map(person => {
            const personData = person.attributes || person
            const entityId = person.id || personData.id
            return {
              id: entityId,
              name: personData.name || person.display_name,
              belongable_type: 'Person',
              previously_deployed: deployedData.quantities[entityId] || 0,
              deployment_details: deployedData.details[entityId] || [],
              deploy_quantity: 0,
            }
          })
          console.log('entities', this.entities)
      } catch (error) {
        console.error('Error loading leader roles:', error)
        // Fallback: show empty or try alternative approach
        this.entities = []
      } finally {
        this.loading = false
      }
    },
    async loadTrainingCenters() {
      // For TrainingCenterGroup, we use belongableId; for Country, we use countryId
      if (this.belongableType === 'TrainingCenterGroup') {
        if (!this.belongableId) {
          console.error('Belongable ID is required to load training centers for TrainingCenterGroup')
          this.entities = []
          return
        }
      } else if (!this.countryId) {
        console.error('Country ID is required to load training centers')
        this.entities = []
        return
      }
      
      this.loading = true
      try {
        const params = {
          format: 'json',
          serializer_format: 'extended',
        }
        
        // Apply state filter (default to active if not selected)
        params.state = this.selectedState?.value || 'active'
        
        // For TrainingCenterGroup, filter by the group ID directly
        if (this.belongableType === 'TrainingCenterGroup') {
          params.training_center_group_id = parseInt(this.belongableId, 10)
        } else {
          // For Country, use country_id and optional training center group filter
          params.country_id = this.countryId
          
          // Apply training center group filter if selected
          if (this.selectedTrainingCenterGroup) {
            const groupId = this.selectedTrainingCenterGroup.id || this.selectedTrainingCenterGroup
            if (groupId) {
              params.training_center_group_id = parseInt(groupId, 10)
            }
          }
        }
        
        // Apply start date range filters
        if (this.startDateFrom) {
          params.start_date_start = this.startDateFrom
        }
        if (this.startDateTo) {
          params.start_date_end = this.startDateTo
        }
        
        // Apply reporting milestone filter
        if (this.selectedReportingMilestone) {
          params.reporting_milestone_id = this.selectedReportingMilestone.id
        }
        
        // Apply timothies range filters
        if (this.timothiesMin !== null && this.timothiesMin !== '') {
          params.timothies_min = this.timothiesMin
        }
        if (this.timothiesMax !== null && this.timothiesMax !== '') {
          params.timothies_max = this.timothiesMax
        }
        
        // Apply sort
        if (this.selectedSort) {
          params.sort = this.selectedSort.value
        }
        
        let response = await axios.get('/training_centers.json', { params })
        
        // Handle different response formats
        let trainingCenters = Array.isArray(response.data) 
          ? response.data 
          : (response.data.data || [])
        
        // For TrainingCenterGroup: if no active TCs found and no explicit state filter was selected,
        // retry with ALL_COUNTED_STATES (active, suspended, graduated)
        if (this.belongableType === 'TrainingCenterGroup' && trainingCenters.length === 0 && !this.selectedState) {
          params.state = 'active,suspended,graduated' // ALL_COUNTED_STATES
          response = await axios.get('/training_centers.json', { params })
          trainingCenters = Array.isArray(response.data) 
            ? response.data 
            : (response.data.data || [])
        }
        
        // Get deployed quantities for training centers
        const deployedData = await this.getDeployedQuantities('TrainingCenter')
        
        this.entities = trainingCenters.map(tc => {
          const tcData = tc.attributes || tc
          const entityId = tc.id || tcData.id
          return {
            id: entityId,
            name: tcData.name || tc.name,
            belongable_type: 'TrainingCenter',
            previously_deployed: deployedData.quantities[entityId] || 0,
            deployment_details: deployedData.details[entityId] || [],
            deploy_quantity: 0,
            start_date: tcData.start_date || tc.start_date,
            reporting_milestone_name: tcData.reporting_milestone_name || tc.reporting_milestone_name,
            timothy_count: tcData.timothy_count ?? tc.timothy_count,
          }
        })
      } catch (error) {
        console.error('Error loading training centers:', error)
        this.entities = []
      } finally {
        this.loading = false
      }
    },
    async getDeployedQuantities(belongableType) {
      try {
        // Get existing deployed resources for this parent deployment
        const response = await axios.get(`/resource_deployments/${this.resourceDeploymentId}.json`)
        const deployment = response.data
        
        const result = { quantities: {}, details: {} }
        if (deployment.deployed_resources) {
          deployment.deployed_resources
            .filter(dr => dr.belongable_type === belongableType)
            .forEach(dr => {
              const id = dr.belongable_id
              result.quantities[id] = (result.quantities[id] || 0) + dr.quantity
              if (!result.details[id]) {
                result.details[id] = []
              }
              result.details[id].push({
                quantity: dr.quantity,
                deployed_at: dr.deployed_at
              })
            })
        }
        
        return result
      } catch (error) {
        console.error('Error getting deployed quantities:', error)
        return { quantities: {}, details: {} }
      }
    },
    async loadEntities() {
      if (this.deploymentType === 'leader_roles') {
        await this.loadLeaderRoles()
      } else {
        await this.loadTrainingCenters()
      }
    },
    onDeploymentTypeChange() {
      this.selectedEntityIds = []
      this.bulkQuantity = null
      this.bulkDeployMode = 'fixed'
      this.bulkDeployMessage = null
      this.loadEntities()
    },
    onFilterChange() {
      if (this.deploymentType === 'training_centers') {
        this.selectedEntityIds = []
        this.bulkDeployMessage = null
        this.loadEntities()
      }
    },
    clearFilters() {
      this.selectedTrainingCenterGroup = null
      this.selectedState = null
      this.selectedReportingMilestone = null
      this.selectedSort = { value: 'name', name: 'Name' }
      this.startDateFrom = null
      this.startDateTo = null
      this.timothiesMin = null
      this.timothiesMax = null
      this.filterNoPreviousDeployment = false
      this.selectedEntityIds = []
      this.loadEntities()
    },
    onLeaderFilterChange() {
      this.selectedEntityIds = []
      this.loadLeaderRoles()
    },
    clearLeaderFilters() {
      this.selectedLeaderState = null
      this.selectedLeaderRoles = []
      this.filterLeaderNoPreviousDeployment = false
      this.selectedEntityIds = []
      this.loadLeaderRoles()
    },
    getSortValue(entity) {
      if (!this.selectedSort) return ''
      switch (this.selectedSort.value) {
        case 'start_date':
          if (!entity.start_date) return '-'
          // Strip time portion, show only date
          return entity.start_date.split('T')[0]
        case 'reporting_milestone':
          return entity.reporting_milestone_name || '-'
        case 'num_timothies':
          return entity.timothy_count ?? '-'
        default:
          return ''
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedEntityIds = []
      } else {
        this.selectedEntityIds = this.filteredEntities.map(e => e.id)
      }
    },
    applyBulkQuantity() {
      const bulkQty = this.bulkQuantity || 0
      const totalSelected = this.selectedEntityIds.length
      
      // Calculate quantity already assigned to non-selected entities
      const assignedToOthers = this.entities
        .filter(e => !this.selectedEntityIds.includes(e.id))
        .reduce((sum, e) => sum + (e.deploy_quantity || 0), 0)
      
      // Calculate remaining available quantity
      let remainingQuantity = this.availableQuantity - assignedToOthers
      
      // Track how many entities received full, partial, or no allocation
      let fullAllocationCount = 0
      let partialAllocationCount = 0
      let noAllocationCount = 0
      
      // Apply bulk quantity to selected entities until quantity is depleted
      this.entities.forEach(entity => {
        if (this.selectedEntityIds.includes(entity.id)) {
          // Calculate quantity for this entity based on deploy mode
          let entityQty = bulkQty
          if (this.bulkDeployMode === 'per_timothy') {
            // Multiply by timothy count for per_timothy mode
            entityQty = bulkQty * (entity.timothy_count || 0)
          }
          
          if (remainingQuantity >= entityQty) {
            entity.deploy_quantity = entityQty
            remainingQuantity -= entityQty
            if (entityQty > 0) {
              fullAllocationCount++
            } else {
              noAllocationCount++ // 0 timothies in per_timothy mode
            }
          } else if (remainingQuantity > 0) {
            // Assign remaining quantity (partial)
            entity.deploy_quantity = remainingQuantity
            remainingQuantity = 0
            partialAllocationCount++
          } else {
            // No quantity left, set to 0
            entity.deploy_quantity = 0
            noAllocationCount++
          }
        }
      })
      
      // Set message about bulk deploy results
      if (noAllocationCount > 0 || partialAllocationCount > 0) {
        this.bulkDeployMessage = {
          totalSelected,
          fullAllocationCount,
          partialAllocationCount,
          noAllocationCount
        }
      } else {
        this.bulkDeployMessage = null
      }
      
      this.bulkQuantity = null
      this.selectedEntityIds = [] // Clear selected checkboxes after applying quantity
    },
    clearSelectedQuantities() {
      this.entities.forEach(entity => {
        if (this.selectedEntityIds.includes(entity.id)) {
          entity.deploy_quantity = 0
        }
      })
      this.selectedEntityIds = [] // Clear selected checkboxes after clearing quantities
    },
    onCancel() {
      window.location.href = `/resource_deployments/${this.resourceDeploymentId}`
    },
    getMaxDeployQuantity(entity) {
      const allocated = this.totalAllocatedQuantity || 0
      const previouslyDeployed = entity.previously_deployed || 0
      return Math.max(0, allocated - previouslyDeployed)
    },
    formatDeploymentDetailsTooltip(details) {
      if (!details || details.length === 0) return ''
      return details.map(d => {
        const date = d.deployed_at ? new Date(d.deployed_at).toLocaleDateString() : 'N/A'
        return `Qty: ${d.quantity}, Date: ${date}`
      }).join('\n')
    },
    getDeployQuantityError(entity) {
      if (!entity.deploy_quantity || entity.deploy_quantity <= 0) {
        return false
      }
      const maxQuantity = this.getMaxDeployQuantity(entity)
      return entity.deploy_quantity > maxQuantity
    },
    hasValidationErrors() {
      const totalNewDeployment = this.entities
        .filter(e => e.deploy_quantity && e.deploy_quantity > 0)
        .reduce((sum, e) => sum + (e.deploy_quantity || 0), 0)
      
      // Check total doesn't exceed available quantity
      if (totalNewDeployment > this.availableQuantity) {
        return true
      }
      
      // Check individual entities don't exceed their max
      return this.entities.some(e => this.getDeployQuantityError(e))
    },
    getTotalDeploymentQuantity() {
      return this.entities
        .filter(e => e.deploy_quantity && e.deploy_quantity > 0)
        .reduce((sum, e) => sum + (e.deploy_quantity || 0), 0)
    },
    async onDeploy() {
      // New resource_deployment entities created will have parent_resource_deployment_id set to the parent
      // The parent_resource_deployment_id should be set to the resource deployment we're deploying from
      // They also inherit resource_id and resource_donation_id from the parent
      const parentId = this.parentResourceDeploymentId || this.resourceDeploymentId
      
      const deployedResources = this.entities
        .filter(e => e.deploy_quantity && e.deploy_quantity > 0)
        .map(e => ({
          belongable_id: e.id,
          belongable_type: e.belongable_type,
          quantity: e.deploy_quantity,
          deployed_at: this.deploymentDate,
          parent_resource_deployment_id: parentId, // Set to parent allocation's ID
          resource_id: this.resourceId, // Inherited from parent
          resource_donation_id: this.resourceDonationId, // Inherited from parent
        }))
      
      if (deployedResources.length === 0) {
        alert('Please enter quantities for at least one entity.')
        return
      }
      
      if (!this.deploymentDate) {
        alert('Please select a deployment date.')
        return
      }
      
      // Validate quantities don't exceed available allocation
      const totalNewDeployment = deployedResources.reduce((sum, dr) => sum + dr.quantity, 0)
      const availableQuantity = this.availableQuantity
      
      if (totalNewDeployment > availableQuantity) {
        alert(`Total deployment quantity (${totalNewDeployment}) exceeds available quantity (${availableQuantity}).`)
        return
      }
      
      // Validate individual entities
      const invalidEntities = this.entities.filter(e => this.getDeployQuantityError(e))
      if (invalidEntities.length > 0) {
        const entityNames = invalidEntities.map(e => e.name).join(', ')
        alert(`Deployment quantities exceed available quantity for: ${entityNames}`)
        return
      }
      
      try {
        const payload = {
          resource_deployment: {
            deployed_resources_attributes: deployedResources.reduce((acc, dr, index) => {
              acc[index] = dr
              return acc
            }, {}),
          },
        }
        
        const response = await axios.patch(
          `/resource_deployments/${this.resourceDeploymentId}`,
          payload,
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            }
          }
        )
        
        this.$emit('deployed', response.data)
        
        // Redirect or show success message
        if (response.data.id) {
          window.location.href = `/resource_deployments/${response.data.id}`
        }
      } catch (error) {
        console.error('Error deploying resources:', error)
        
        // Handle validation errors from server
        let errorMessage = 'An error occurred while deploying resources.'
        if (error.response?.data) {
          const errorData = error.response.data
          if (errorData.error) {
            errorMessage = errorData.error
          } else if (errorData.errors) {
            // Handle errors array (full_messages format)
            if (Array.isArray(errorData.errors)) {
              errorMessage = errorData.errors.join('\n')
            } else if (errorData.errors.base) {
              errorMessage = Array.isArray(errorData.errors.base) 
                ? errorData.errors.base.join(', ')
                : errorData.errors.base
            } else {
              errorMessage = JSON.stringify(errorData.errors)
            }
          }
        }
        
        alert(errorMessage)
      }
    },
  },
}
</script>

<style scoped>
.resource-deployment-deploy {
  min-height: 400px;
}

.table-less-column-padding-sticky {
  width: 100%;
  border-collapse: collapse;
}

.table-less-column-padding-sticky th,
.table-less-column-padding-sticky td {
  padding: 0.5rem;
}

.quantBoxes {
  max-width: 100px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 4rem;
  z-index: 10;
  border-radius: 0.375rem;
}
</style>

<style>
/* Allow multiselect dropdowns to overflow their containers */
.resource-deployment-deploy .multiselect__content-wrapper {
  z-index: 1000 !important;
}

.resource-deployment-deploy .multiselect__content {
  z-index: 1000 !important;
}
</style>
